import OpMachineDTO from "@common/dtos/OpMachineDTO";
import { MDBBtn, MDBIcon, MDBListGroup, MDBListGroupItem, MDBSpinner } from "mdb-react-ui-kit";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CustomModal } from "../../CustomModal";
import CashboxTable from "./CashboxTable";
import API from "../../../service/api";
import { Alert } from "../../Alert";
import { useTranslation } from "react-i18next";

export interface CashboxModalProps {
    machine: OpMachineDTO;
    apiURI: string;
}

interface CashboxCellDef {
    coins: number;
    quantity: number;
    isExtra: boolean;
}

export default function CashboxModal({
    machine,
    apiURI
}: CashboxModalProps){
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [data, setData] = useState<CashboxCellDef[]>([]); 
    const [initialData, setInitialData] = useState<CashboxCellDef[]>([]);
    const [error, setError] = useState('');
    const total = useMemo(() => data.reduce((prev, crt) => prev + (crt.coins * crt.quantity), 0), [data]);
    const valid = useMemo(() => total == machine.value, [total, machine.value]);

    const onDataChanged = useCallback((coins: number, quantity: number) => {
        setData(prev => prev.map(el => el.coins !== coins ? el : ({
            ...el,
            quantity
        })));
        setError('');
    }, []);

    const onSubmit = useCallback(() => {
        setSubmitting(true);
        const cashier: {[key: number]: number} = {};
        initialData.forEach(el => { cashier[el.coins] = el.quantity; });
        const cashierAdjusted: {[key: number]: number} = {};
        data.forEach(el => { cashierAdjusted[el.coins] = el.quantity; });
        API.POST(`${apiURI}/cashier`, {
            cashier,
            cashierAdjusted
        })
            .then(() => {
                setOpen(false);
            })
            .catch(err => setError(err.message))
            .finally(() => setSubmitting(false));
    }, [apiURI, data, initialData]);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        setSubmitting(false);
        setData([]);
        setInitialData([]);
        setError('');

        API.GET(`${apiURI}/cashier`)
            .then((values: CashboxCellDef[]) => {
                setData(values);
                setInitialData(JSON.parse(JSON.stringify(values)));
                setLoading(false);
            })
            .catch(err => setError(err.message));
    }, [open]);
 
    // TODO: verifica daca masina e online..
    if (
        machine.type?.id !== 3 ||
        typeof machine.value !== 'number' ||
        !machine.permissions.ClearMoneyBox ||
        !machine.connection
    ) return null;
    return (
        <>
            <span role="button" onClick={() => setOpen(true)}>
                <MDBIcon icon="piggy-bank" color="primary" size="lg" className="ms-1"/>
            </span>

            <CustomModal
                open={open}
                onClose={() => setOpen(false)}
                title={t('cashier')}
                
            >
                <CashboxTable
                    data={data}
                    onDataChanged={onDataChanged}
                    loading={loading}
                />
                { !loading && <MDBListGroup light className="p-0">
                    <MDBListGroupItem className="d-flex flex-row align-items-center justify-content-between p-1 p-1">
                        <span>{t('total')}</span>
                        
                        <span className="d-flex flex-row align-items-center">
                            <span className="badge badge-danger me-1" style={{display: valid ? 'none' : 'flex'}}>
                                <MDBIcon icon="exclamation-triangle me-1"/>
                                {t('invalid')}
                            </span>
                            {total}
                        </span>
                    </MDBListGroupItem>
                    <MDBListGroupItem className="d-flex flex-row align-items-center justify-content-between p-1" style={{}}>
                        <span>{t('value')}</span>
                        <span>{machine.value}</span>
                    </MDBListGroupItem>
                </MDBListGroup> }

                { !loading && <div className="d-flex justify-content-end">
                    <MDBBtn color="success" disabled={!valid || submitting} onClick={onSubmit} className="d-flex flex-row align-items-center">
                        {submitting && (
                            <MDBSpinner size="sm" className="me-1"/>
                        )}
                        {t('confirm')}
                    </MDBBtn>
                </div> }

                <Alert type="danger" message={error}/>
                
            </CustomModal>
        </>
    )
}